import { useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { format, isToday, parseISO, isValid } from "date-fns";
import { Loader2, MapPin, Phone, Mail, ChevronLeft, ChevronRight, Calendar, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type Show = {
  id: string;
  showDate: string;
  eventTime?: string | null;
  endTime?: string | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venueCity?: string | null;
  venueState?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  notes?: string | null;
  status?: string | null;
  talentName?: string | null;
  showType?: string | null;
  packagePrice?: number | null;
  arrivedAt?: string | null;
  completedAt?: string | null;
};

function dayKey(d: Date) {
  return format(d, "yyyy-MM-dd");
}

// Parse a `YYYY-MM-DD` string in the local timezone (avoiding the UTC
// drift you get from `new Date("2026-05-15")`). Returns `null` for any
// malformed or impossible date so callers can fall back to today.
// Rejects rolled-over dates like `2026-02-31` by round-tripping the
// parsed components instead of trusting `Date`'s lenient overflow.
function parseDayParam(value: string | null): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(year, month - 1, day);
  if (!isValid(d)) return null;
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
}

export default function RunsheetPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const search = useSearch();

  // The selected day lives in the URL (`?day=YYYY-MM-DD`) so it can be
  // deep-linked, shared, and walked through with the browser back/forward
  // buttons. An invalid or missing param falls back to today.
  const day = useMemo(() => {
    const fromUrl = parseDayParam(new URLSearchParams(search).get("day"));
    return fromUrl ?? new Date();
  }, [search]);

  const setDay = (next: Date) => {
    navigate(`/runsheet?day=${dayKey(next)}`);
  };

  const { data: shows = [], isLoading } = useQuery<Show[]>({
    queryKey: ["/api/runsheet", dayKey(day)],
    queryFn: async () => {
      const r = await fetch(`/api/runsheet?day=${encodeURIComponent(dayKey(day))}`, { credentials: "include" });
      if (!r.ok) throw new Error(`runsheet ${r.status}`);
      return r.json();
    },
  });

  const checkIn = useMutation({
    mutationFn: async (showId: string) => apiRequest("POST", `/api/runsheet/${showId}/arrived`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/runsheet"] });
      toast({ title: "Checked in" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e?.message, variant: "destructive" }),
  });

  const complete = useMutation({
    mutationFn: async (showId: string) => apiRequest("POST", `/api/runsheet/${showId}/completed`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/runsheet"] });
      toast({ title: "Marked complete" });
    },
    onError: (e: any) => toast({ title: "Failed", description: e?.message, variant: "destructive" }),
  });

  const sorted = useMemo(
    () => [...shows].sort((a, b) => (a.eventTime || "").localeCompare(b.eventTime || "")),
    [shows]
  );

  return (
    <div className="flex flex-col h-full pb-20" data-testid="page-runsheet">
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="icon"
          data-testid="button-runsheet-prev-day"
          onClick={() => setDay(new Date(day.getTime() - 86400000))}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex flex-col items-center">
          <div className="text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Run sheet
          </div>
          <div className="text-base font-semibold" data-testid="text-runsheet-day">
            {isToday(day) ? "Today" : format(day, "EEE, MMM d")}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          data-testid="button-runsheet-next-day"
          onClick={() => setDay(new Date(day.getTime() + 86400000))}
        >
          <ChevronRight className="h-5 w-5" />
        </Button>
      </header>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 p-6">
          <Calendar className="h-12 w-12 opacity-40" />
          <div className="text-sm">No shows on this day.</div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto px-3 py-3 space-y-3">
          {sorted.map((s) => (
            <RunsheetCard
              key={s.id}
              show={s}
              onCheckIn={() => checkIn.mutate(s.id)}
              onComplete={() => complete.mutate(s.id)}
              checkingIn={checkIn.isPending}
              completing={complete.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RunsheetCard({
  show,
  onCheckIn,
  onComplete,
  checkingIn,
  completing,
}: {
  show: Show;
  onCheckIn: () => void;
  onComplete: () => void;
  checkingIn: boolean;
  completing: boolean;
}) {
  const addressFull = [show.venueAddress, show.venueCity, show.venueState].filter(Boolean).join(", ");
  const mapsUrl = addressFull
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressFull)}`
    : null;

  return (
    <Card data-testid={`card-runsheet-show-${show.id}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-base font-semibold truncate" data-testid={`text-runsheet-time-${show.id}`}>
              {show.eventTime || "TBD"}
              {show.endTime ? ` – ${show.endTime}` : ""}
            </div>
            <div className="text-sm font-medium truncate">{show.venueName || "Untitled venue"}</div>
            {show.talentName && (
              <div className="text-xs text-muted-foreground truncate">{show.talentName}</div>
            )}
          </div>
          <StatusPill show={show} />
        </div>

        {addressFull && (
          <div className="text-sm text-muted-foreground flex items-start gap-1.5">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span className="break-words">{addressFull}</span>
          </div>
        )}

        {(show.contactPhone || show.contactEmail) && (
          <div className="flex flex-wrap gap-2">
            {show.contactPhone && (
              <a href={`tel:${show.contactPhone}`} data-testid={`link-runsheet-call-${show.id}`}>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Phone className="h-4 w-4" />
                  Call
                </Button>
              </a>
            )}
            {show.contactEmail && (
              <a href={`mailto:${show.contactEmail}`} data-testid={`link-runsheet-email-${show.id}`}>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Mail className="h-4 w-4" />
                  Email
                </Button>
              </a>
            )}
            {mapsUrl && (
              <a href={mapsUrl} target="_blank" rel="noopener" data-testid={`link-runsheet-directions-${show.id}`}>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <MapPin className="h-4 w-4" />
                  Directions
                </Button>
              </a>
            )}
          </div>
        )}

        {show.notes && (
          <div className="text-xs bg-muted/50 rounded-md p-2 whitespace-pre-wrap" data-testid={`text-runsheet-notes-${show.id}`}>
            {show.notes}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          {!show.arrivedAt && (
            <Button
              size="sm"
              variant="default"
              onClick={onCheckIn}
              disabled={checkingIn}
              className="flex-1"
              data-testid={`button-runsheet-checkin-${show.id}`}
            >
              {checkingIn ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Check in
            </Button>
          )}
          {show.arrivedAt && !show.completedAt && (
            <Button
              size="sm"
              variant="default"
              onClick={onComplete}
              disabled={completing}
              className="flex-1"
              data-testid={`button-runsheet-complete-${show.id}`}
            >
              {completing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Mark complete
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ show }: { show: Show }) {
  if (show.completedAt) {
    return (
      <Badge variant="secondary" className="shrink-0" data-testid={`status-runsheet-${show.id}`}>
        <CheckCircle2 className="h-3 w-3 mr-1" /> Done
      </Badge>
    );
  }
  if (show.arrivedAt) {
    return (
      <Badge className="shrink-0 bg-green-600 hover:bg-green-600" data-testid={`status-runsheet-${show.id}`}>
        On site
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="shrink-0" data-testid={`status-runsheet-${show.id}`}>
      Upcoming
    </Badge>
  );
}
